import { Component, computed, effect, EventEmitter, input, OnInit, Output, output, inject, ChangeDetectionStrategy } from '@angular/core';
import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { ActivatedRoute, Router } from '@angular/router';
import { WorkGridItemObject } from '@libs/types/src/services/object-type/work/work-grid.object';
import { isFolderTerminated } from '@libs/types/src/services/enum/folder-status.enum';
import { map } from 'rxjs/operators';
import { FormGroup } from '@angular/forms';
import { goBack } from '../../../../../modules/shared/shared.global-service';
import { Splitter, SplitterResizeEndEvent } from '@openng/optimus-ui/splitter';
import { PrimeTemplate } from '@openng/optimus-ui/api';
import { NgTemplateOutlet } from '@angular/common';
import { ButtonComponent } from '../../../../kit/button/button.component';
import { WorkCardComponent } from '../../../../../modules/content/components/services/work-card/work-card.component';
import { WarnFloatMessageComponent } from '../../../../kit/float-message/warn-float-message.component';
import { ActionButtonsComponent } from '../../../../kit/action-buttons/action-buttons.component';
import { ShortDatePipe } from '../../../../../modules/shared/pipes/short-date.pipe';

const DEFAULT_PANEL_SIZES = [60, 40];

@Component({
    selector: 'app-work-form-container',
    templateUrl: 'work-form-container.component.html',
    styleUrls: ['work-form-container.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        Splitter,
        PrimeTemplate,
        NgTemplateOutlet,
        ButtonComponent,
        WorkCardComponent,
        WarnFloatMessageComponent,
        ActionButtonsComponent,
        ShortDatePipe,
    ],
})
/**
 * @Deprecated
 * This is totally butchered at any cost DO NOT take this as an example for future development
 * Removed in IndividualWorkForm and used splitter without this shit
 */
export class WorkFormContainerComponent implements OnInit {
    private breakpointObserver = inject(BreakpointObserver);
    private readonly route = inject(ActivatedRoute);
    private readonly router = inject(Router);

    existingWork = input<WorkGridItemObject | null>(null);
    form = input<FormGroup | null>(null);
    folderClosedAt = input<Date | string | null>(null);
    disableSaveButton = input(false);
    workDataFieldsToCopy = input<string[] | null>(null);

    protected readonly folderTerminated = computed(() => isFolderTerminated({
        closedAt: this.folderClosedAt(),
    }));

    submit = output<void>();
    @Output() closeCallback = new EventEmitter<void>();

    protected workPanelSize: number = DEFAULT_PANEL_SIZES[0];
    protected splittedPanelSize: number = DEFAULT_PANEL_SIZES[1];

    layout: string = 'vertical';

    constructor() {
        // re-open splitter when a new existing work is selected
        effect(() => {
            this.existingWork();
            if (this.splitterClosed) {
                this.splitterClosed = false;
                this.workPanelSize = DEFAULT_PANEL_SIZES[0];
                this.splittedPanelSize = DEFAULT_PANEL_SIZES[1];
            }
        });
    }

    ngOnInit() {
        this.initBreakpointObserver();
    }

    // Changes layout according to size of the screen
    private initBreakpointObserver() {
        this.breakpointObserver.observe([Breakpoints.TabletPortrait, Breakpoints.Handset])
            .pipe(
                map(result => {
                    if (result.matches) {
                        this.layout = 'vertical';
                    } else {
                        this.layout = 'horizontal';
                    }
                }),
            )
            .subscribe();
    }

    protected splitterClosed = false;

    // Should we use splitter
    get useSplitter() {
        return this.route.snapshot.queryParams['split'] && !this.splitterClosed;
    }

    protected copyTextToForm(text: string): void {
        if ('note' in this.form()?.value) {
            const currentContent = this.form()?.value.note;
            this.form()?.patchValue({
                note: (currentContent ? currentContent + '\n' : '') + text,
            });
        } else if ('description' in this.form()?.value) { // quickfix for operational reports
            const currentContent = this.form()?.value.description;
            this.form()?.patchValue({
                description: (currentContent ? currentContent + '\n' : '') + text,
            });
        } else if ('data' in this.form()?.value) { // quickfix for operational reports
            const currentContent = this.form()?.value.data;
            this.form()?.patchValue({
                data: (currentContent ? currentContent + '\n' : '') + text,
            });
        }
    }

    protected copyTextToFormField({ text, field }: { text: string; field: string }): void {
        if (field in this.form()?.value) {
            const currentContent = this.form()?.value[field];
            this.form()?.patchValue({
                [field]: (currentContent ? currentContent + '\n' : '') + text,
            });
        }
    }

    protected copyWorkDataToForm() {
        const workData = this.existingWork()?.workData || null;
        if (this.workDataFieldsToCopy() && workData) {
            const valuesToCopy: any = {};
            this.workDataFieldsToCopy()?.forEach(field => {
                if (field in workData) {
                    valuesToCopy[field] = (workData as any)[field];
                }
            });
            this.form()?.patchValue(valuesToCopy);
        }
    }

    public onResize(event: SplitterResizeEndEvent) {
        this.workPanelSize = Number(event.sizes[0]);
        this.splittedPanelSize = Number(event.sizes[1]);
    }

    protected closeSplittedPanel() {
        this.splitterClosed = true;
        this.router.navigate([], {
            relativeTo: this.route,
            queryParams: { split: null },
            queryParamsHandling: 'merge',
        });
    }

    // Close button function
    protected close() {
        if (this.closeCallback.observed) {
            this.closeCallback.emit();
        } else {
            goBack();
        }
    }
}
